import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Spin,
  Button,
  Input,
  Row,
  Col,
  message,
  Checkbox,
  Select,
  DatePicker,
  Upload,
  Modal,
} from "antd";
import dayjs from "dayjs";
import { UploadOutlined } from "@ant-design/icons";
import { CloseOutlined } from "@ant-design/icons";
import { getRequest, postRequest } from "../utils/apicalls";
import "../styles/internprofile.css";

const InternProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [intern, setIntern] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);

  const fetchIntern = async () => {
    setLoading(true);

    try {
      const response = await getRequest(`interns/get/${id}`);

      let data = null;

      if (response?.data?.data) {
        data = Array.isArray(response.data.data)
          ? response.data.data[0]
          : response.data.data;
      } else if (response?.data) {
        data = Array.isArray(response.data) ? response.data[0] : response.data;
      } else {
        data = response;
      }

      setIntern(data);

      setFormData({
        first_name: data?.first_name || "",
        middle_name: data?.middle_name || "",
        last_name: data?.last_name || "",
        suffix: data?.suffix || "",
        type: data?.type || "",
        school: data?.school || "",
        course_program: data?.course_program || "",
        company_email: data?.company_email || "",
        contact_number: data?.contact_number || "",
        name_of_coordinator: data?.name_of_coordinator || "",
        number_of_coordinator: data?.number_of_coordinator || "",
        email_of_coordinator: data?.email_of_coordinator || "",
        batch_number: data?.batch_number || "",
        hours_required: data?.hours_required || "",
        nda: Number(data?.nda) === 1,
        moa: Number(data?.moa) === 1,
        sic: Number(data?.sic) === 1,
        study_load: Number(data?.study_load) === 1,
        month_interview: Number(data?.month_interview) === 1,
        is_staffhouse: Number(data?.is_staffhouse) === 1,
        move_in_date: data?.move_in_date || "",
        move_out_date: data?.move_out_date || "",
        move_out_form: data?.move_out_form || "",
        personal_email: data?.personal_email || "",
      });
    } catch (err) {
      console.error(err);
      message.error("Failed to load intern profile");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIntern();
  }, [id]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCheckboxChange = (field, checked) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: checked };

      if (field === "is_staffhouse" && !checked) {
        updated.move_in_date = "";
        updated.move_out_date = "";
        updated.move_out_form = "";
        setSelectedFile(null);
      }

      return updated;
    });
  };

  const handleSave = async () => {
    const hours = parseInt(formData.hours_required, 10);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.first_name.trim()) {
      message.error("First Name is required");
      return;
    }

    if (!formData.last_name.trim()) {
      message.error("Last Name is required");
      return;
    }

    if (!formData.course_program.trim()) {
      message.error("Course / Program is required");
      return;
    }
    if (!formData.personal_email || !emailRegex.test(formData.personal_email)) {
      message.error("Please enter a valid personal email");
      return;
    }

    if (!formData.company_email || !emailRegex.test(formData.company_email)) {
      message.error("Please enter a valid company email");
      return;
    }
    if (
      !formData.email_of_coordinator ||
      !emailRegex.test(formData.email_of_coordinator)
    ) {
      message.error("Please enter a valid coordinator email");
      return;
    }

    if (isNaN(hours)) {
      message.error("Hours Required must be a valid number");
      return;
    }

    if (hours <= 0) {
      message.error("Hours Required must be greater than 0");
      return;
    }

    if (formData.contact_number.length !== 11) {
      message.error("Contact Number must be exactly 11 digits");
      return;
    }

    if (formData.number_of_coordinator.length !== 11) {
      message.error("Coordinator Contact must be exactly 11 digits");
      return;
    }

    try {
      setLoading(true);

      const payload = new FormData();

      Object.keys(formData).forEach((key) => {
        if (key !== "move_out_form") {
          payload.append(key, formData[key]);
        }
      });

      payload.set("hours_required", hours);
      payload.set("nda", formData.nda ? 1 : 0);
      payload.set("moa", formData.moa ? 1 : 0);
      payload.set("sic", formData.sic ? 1 : 0);
      payload.set("study_load", formData.study_load ? 1 : 0);
      payload.set("month_interview", formData.month_interview ? 1 : 0);
      payload.set("is_staffhouse", formData.is_staffhouse ? 1 : 0);

      if (formData.is_staffhouse) {
        payload.set("move_in_date", formData.move_in_date);
        payload.set("move_out_date", formData.move_out_date);

        if (selectedFile) {
          payload.append("move_out_form", selectedFile);
        }
      }

      await postRequest(`interns/update/${id}`, payload, true);

      message.success("Updated successfully!");
      setEditing(false);
      fetchIntern();
    } catch (err) {
      console.error(err);
      message.error("Update failed");
    } finally {
      setLoading(false);
    }
  };

  const imageUrl = formData.move_out_form?.startsWith("blob:")
    ? formData.move_out_form
    : `http://localhost/intern-attendance-tracker-api/${formData.move_out_form}`;

  return (
    <div className="profile-overlay">
      <Spin spinning={loading}>
        <div className="profile-modal">
          {/* CLOSE BUTTON */}
          <button className="close-btn" onClick={() => navigate(-1)}>
            <CloseOutlined />
          </button>

          {/* TITLE */}
          <h2 className="profile-title">
            {formData.first_name} {formData.last_name}
          </h2>

          {/* PERSONAL INFORMATION */}
          <Card className="profile-card">
            <h3 className="section-title">PERSONAL INFORMATION</h3>

            <Row gutter={16}>
              <Col span={6}>
                <label>
                  First Name <span className="required">*</span>
                </label>
                <Input
                  value={formData.first_name}
                  disabled={!editing}
                  onChange={(e) => handleChange("first_name", e.target.value)}
                />
              </Col>

              <Col span={6}>
                <label>Middle Name</label>
                <Input
                  value={formData.middle_name}
                  disabled={!editing}
                  onChange={(e) => handleChange("middle_name", e.target.value)}
                />
              </Col>

              <Col span={6}>
                <label>
                  Last Name <span className="required">*</span>
                </label>
                <Input
                  value={formData.last_name}
                  disabled={!editing}
                  onChange={(e) => handleChange("last_name", e.target.value)}
                />
              </Col>

              <Col span={6}>
                <label>Suffix</label>
                <Input
                  value={formData.suffix}
                  disabled={!editing}
                  onChange={(e) => handleChange("suffix", e.target.value)}
                />
              </Col>
            </Row>

            <Row gutter={16} className="mt">
              <Col span={8}>
                <label>
                  Type <span className="required">*</span>
                </label>
                <Select
                  value={formData.type}
                  disabled={!editing}
                  style={{ width: "100%" }}
                  onChange={(value) => handleChange("type", value)}
                >
                  <Select.Option value="College">College</Select.Option>
                  <Select.Option value="HS">HS</Select.Option>
                </Select>
              </Col>

              <Col span={8}>
                <label>
                  School <span className="required">*</span>
                </label>
                <Input
                  value={formData.school}
                  disabled={!editing}
                  onChange={(e) => handleChange("school", e.target.value)}
                />
              </Col>

              <Col span={8}>
                <label>
                  Course / Program <span className="required">*</span>
                </label>
                <Input
                  value={formData.course_program}
                  disabled={!editing}
                  onChange={(e) =>
                    handleChange("course_program", e.target.value)
                  }
                />
              </Col>
            </Row>

            <Row gutter={16} className="mt">
              <Col span={8}>
                <label>
                  Personal Email <span className="required">*</span>
                </label>
                <Input
                  value={formData.personal_email}
                  disabled={!editing}
                  onChange={(e) =>
                    handleChange("personal_email", e.target.value)
                  }
                />
              </Col>
              <Col span={8}>
                <label>
                  Company Email <span className="required">*</span>
                </label>
                <Input
                  value={formData.company_email}
                  disabled={!editing}
                  onChange={(e) =>
                    handleChange("company_email", e.target.value)
                  }
                />
              </Col>

              <Col span={8}>
                <label>
                  Contact Number <span className="required">*</span>
                </label>
                <Input
                  maxLength={11}
                  value={formData.contact_number}
                  disabled={!editing}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    if (value.length <= 11) {
                      handleChange("contact_number", value);
                    }
                  }}
                />
              </Col>
            </Row>

            <Row gutter={16} className="mt">
              <Col span={12}>
                <label>
                  Coordinator Name <span className="required">*</span>
                </label>
                <Input
                  value={formData.name_of_coordinator}
                  disabled={!editing}
                  onChange={(e) =>
                    handleChange("name_of_coordinator", e.target.value)
                  }
                />
              </Col>

              <Col span={12}>
                <label>
                  Coordinator Contact Number <span className="required">*</span>
                </label>
                <Input
                  maxLength={11}
                  value={formData.number_of_coordinator}
                  disabled={!editing}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    if (value.length <= 11) {
                      handleChange("number_of_coordinator", value);
                    }
                  }}
                />
              </Col>
            </Row>

            <Row gutter={16} className="mt">
              <Col span={8}>
                <label>
                  Coordinator's Email <span className="required">*</span>
                </label>
                <Input
                  value={formData.email_of_coordinator}
                  disabled={!editing}
                  onChange={(e) =>
                    handleChange("email_of_coordinator", e.target.value)
                  }
                />
              </Col>

              <Col span={8}>
                <label>
                  Batch Number <span className="required">*</span>
                </label>
                <Input
                  value={formData.batch_number}
                  disabled={!editing}
                  onChange={(e) => handleChange("batch_number", e.target.value)}
                />
              </Col>
              <Col span={8}>
                <label>
                  Hours Required <span className="required">*</span>
                </label>
                <Input
                  value={formData.hours_required}
                  disabled={!editing}
                  onChange={(e) =>
                    handleChange("hours_required", e.target.value)
                  }
                />
              </Col>
            </Row>
            <Row gutter={16} className="mt">
              <Col span={24}>
                <Checkbox
                  checked={formData.is_staffhouse}
                  disabled={!editing}
                  onChange={(e) =>
                    handleCheckboxChange("is_staffhouse", e.target.checked)
                  }
                >
                  Staff House
                </Checkbox>
                {formData.is_staffhouse && (
                  <Row gutter={16} className="mt">
                    <Col span={12}>
                      <label>Move In Date</label>
                      <DatePicker
                        style={{ width: "100%" }}
                        disabled={!editing}
                        value={
                          formData.move_in_date
                            ? dayjs(formData.move_in_date)
                            : null
                        }
                        format="DD/MM/YYYY"
                        onChange={(date, dateString) =>
                          handleChange("move_in_date", dateString)
                        }
                      />
                    </Col>

                    <Col span={12}>
                      <label>Move Out Date</label>
                      <DatePicker
                        style={{ width: "100%" }}
                        disabled={!editing}
                        value={
                          formData.move_out_date
                            ? dayjs(formData.move_out_date)
                            : null
                        }
                        format="DD/MM/YYYY"
                        onChange={(date, dateString) =>
                          handleChange("move_out_date", dateString)
                        }
                      />
                    </Col>
                  </Row>
                )}
              </Col>
            </Row>

            <Row gutter={16} className="mt">
              <Col span={24}>
                <label>Upload Move Out Form</label>

                {/* Visible only while editing */}
                {editing && (
                  <>
                    <Upload
                      beforeUpload={() => false}
                      maxCount={1}
                      accept=".jpg,.jpeg,.png,.pdf"
                      disabled={!editing}
                      showUploadList={false}
                      onChange={({ fileList }) => {
                        if (fileList.length > 0) {
                          const file = fileList[0].originFileObj;

                          setSelectedFile(file);

                          setFormData((prev) => ({
                            ...prev,
                            move_out_form: URL.createObjectURL(file),
                          }));
                        }
                      }}
                    >
                      <Button icon={<UploadOutlined />} disabled={!editing}>
                        Choose File
                      </Button>
                    </Upload>

                    {formData.move_out_form && (
                      <div className="uploaded-file-container">
                        <strong>Uploaded File:</strong>
                        <br />

                        {/\.(jpg|jpeg|png)$/i.test(formData.move_out_form) ? (
                          <img
                            src={imageUrl}
                            alt="Move Out Form"
                            style={{
                              width: 200,
                              marginTop: 10,
                              cursor: "pointer",
                              borderRadius: 8,
                            }}
                            onClick={() => setPreviewVisible(true)}
                          />
                        ) : (
                          <Button
                            type="primary"
                            onClick={() => setPreviewVisible(true)}
                            style={{ marginTop: 10 }}
                          >
                            Preview File
                          </Button>
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* View mode */}
                {!editing && formData.move_out_form && (
                  <Input
                    value={formData.move_out_form.split("/").pop()}
                    disabled
                  />
                )}

                <Modal
                  open={previewVisible}
                  footer={null}
                  onCancel={() => setPreviewVisible(false)}
                  width={900}
                >
                  {/\.(jpg|jpeg|png)$/i.test(formData.move_out_form) ? (
                    <img
                      src={imageUrl}
                      alt="Preview"
                      style={{ width: "100%" }}
                    />
                  ) : (
                    <iframe
                      src={imageUrl}
                      title="Preview"
                      width="100%"
                      height="700px"
                      style={{ border: "none" }}
                    />
                  )}
                </Modal>
              </Col>
            </Row>
          </Card>

          {/* DOCUMENTS */}
          <Card className="profile-card mt">
            <h3 className="section-title">DOCUMENTS</h3>

            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Checkbox
                  checked={formData.nda}
                  disabled={!editing}
                  onChange={(e) =>
                    handleCheckboxChange("nda", e.target.checked)
                  }
                >
                  NDA
                </Checkbox>
              </Col>

              <Col span={8}>
                <Checkbox
                  checked={formData.moa}
                  disabled={!editing}
                  onChange={(e) =>
                    handleCheckboxChange("moa", e.target.checked)
                  }
                >
                  MOA
                </Checkbox>
              </Col>

              <Col span={8}>
                <Checkbox
                  checked={formData.sic}
                  disabled={!editing}
                  onChange={(e) =>
                    handleCheckboxChange("sic", e.target.checked)
                  }
                >
                  SIC
                </Checkbox>
              </Col>

              <Col span={8}>
                <Checkbox
                  checked={formData.study_load}
                  disabled={!editing}
                  onChange={(e) =>
                    handleCheckboxChange("study_load", e.target.checked)
                  }
                >
                  Study Load
                </Checkbox>
              </Col>

              <Col span={8}>
                <Checkbox
                  checked={formData.month_interview}
                  disabled={!editing}
                  onChange={(e) =>
                    handleCheckboxChange("month_interview", e.target.checked)
                  }
                >
                  Month Interview
                </Checkbox>
              </Col>
            </Row>
          </Card>

          {/* BUTTONS */}
          <div className="button-group">
            {!editing ? (
              <Button type="primary" onClick={() => setEditing(true)}>
                Edit
              </Button>
            ) : (
              <>
                <Button
                  onClick={() => {
                    fetchIntern();
                    setEditing(false);
                  }}
                >
                  Cancel
                </Button>
                <Button type="primary" onClick={handleSave}>
                  Save
                </Button>
              </>
            )}
          </div>
        </div>
      </Spin>
    </div>
  );
};

export default InternProfile;
